/**
 * Browser-safe core of the specimen pipeline: the brain's own events decoded,
 * the genome derived from the real BANC annotations, the JEV profile client
 * with its deterministic template fallback, and the art/description helpers the
 * card uses. Shared by web/src/hatch.ts and the CLI tools.
 *
 * o1 used to live here as well (the launch payload and its prepare client). It
 * is gone: a specimen hatches as a SpecimenCard mint, which the app signs
 * through the visitor's own wallet, so nothing in this module talks to an API
 * that could refuse, meter or key a launch.
 */

import { AbiCoder, Interface, keccak256 } from 'ethers'
import { renderSpecimenTemplate } from './template.mjs'
import { binomial, symbol as symbolOf, annotationFromTables } from './genus.mjs'

const coder = AbiCoder.defaultAbiCoder()

// Arc mainnet (chain 5042).
// honors O1_* env overrides to run against a local anvil mock.
export const CHAIN_ID = 5042
export const BRAIN = '0xAEf83c5b8742Da5E3228930Bc6084adcf0539Ccd'
export const OBRAIN = '0x28F986A61E078795639F239675582a12B4cf7F01'
/** The market every hatch launches on: the standard one, quoted in USDC. */
export const JEV_API = 'https://api.typesafe.ai/v1/systemone'

export const BRAIN_STATE_ABI = [
  'event BrainState(uint32 indexed t, int256 p0, int256 p1, int256 p2, int256 p3, uint32 synapses, uint32 spiked, bool capped, bytes32 root, bytes32 poke, bytes fired)',
]

export const brainStateInterface = new Interface(BRAIN_STATE_ABI)

export function decodeBrainState(log) {
  const parsed = brainStateInterface.parseLog({ topics: [...log.topics], data: log.data })
  if (!parsed || parsed.name !== 'BrainState') throw new Error('not a BrainState log')
  const [t, p0, p1, p2, p3, synapses, spiked, capped, root, poke, fired] = parsed.args
  const fh = fired.slice(2)
  const firedIds = []
  for (let i = 0; i + 6 <= fh.length; i += 6) firedIds.push(parseInt(fh.slice(i, i + 6), 16))
  return {
    tick: Number(t),
    potentials: [p0, p1, p2, p3].map(String),
    synapses: Number(synapses),
    spiked: Number(spiked),
    capped: Boolean(capped),
    stateRoot: root,
    poke: poke,
    firedIds,
  }
}

// ---------- spec ----------

export function buildGenome(thought, neuronId, tables) {
  if (!thought.firedIds.includes(neuronId)) {
    throw new Error(`neuron ${neuronId} did not fire in tx ${thought.tx} (fired: ${thought.firedIds.join(', ')})`)
  }
  const ann = annotationFromTables(tables.bin, tables.doc, neuronId)
  const genome = {
    neuronId,
    tx: thought.tx,
    feeder: thought.feeder,
    tick: thought.tick,
    stateRoot: thought.stateRoot,
    firedIds: thought.firedIds,
    firedCount: thought.firedIds.length,
    synapses: thought.synapses,
    spiked: thought.spiked,
    potentials: thought.potentials,
    ...ann,
  }
  genome.name = binomial(genome) // Latin binomial from region/cell_class + hash epithet (D10)
  genome.symbol = symbolOf(genome)
  genome.seed = keccak256(
    coder.encode(['bytes32', 'uint256', 'uint256[]'], [thought.stateRoot, BigInt(neuronId), thought.firedIds.map(BigInt)]),
  )
  return genome
}

export function bytesToBase64(bytes) {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

// ---------- profile (JEV client, template fallback per D8) ----------

export function jevQuestions(genome) {
  return {
    habitat: {
      type: 'choice',
      instructions: 'Which microhabitat does the circuit this neuron belongs to occupy in the fly?',
      criteria: {
        canopy: 'visual / optic lobe circuits, open-air navigation',
        understory: 'central brain association circuits, mushroom body and central complex',
        soil_litter: 'ventral nerve cord motor and sensory leg circuits, ground contact',
      },
    },
    temperament: {
      type: 'score',
      instructions: 'Temperament of this neuron given its anatomy and the firing pattern of its thought',
      criteria: [
        'docile: barely participates, fires late and little',
        'reactive: participates reliably when its channel is charged',
        'volcanic: bursts early, drives its circuit the moment charge arrives',
      ],
    },
    light_averse: {
      type: 'noul',
      instructions: 'This neuron sits in a circuit that suppresses rather than pursues bright stimuli',
    },
    gregarious: {
      type: 'noul',
      instructions: 'This neuron is part of a population code shared with many synchronously firing peers',
    },
  }
}

/**
 * The structured specimen profile: live JEV when a key is configured, the
 * deterministic template otherwise. JEV being down must never block a hatch.
 */
export async function fetchProfile(genome, apiKey) {
  if (!apiKey) {
    return { ...renderSpecimenTemplate(genome), source: 'template fallback: JEV_API_KEY missing' }
  }
  const state = {
    specimen: {
      name: genome.name, symbol: genome.symbol, neuron_id: genome.neuronId,
      region: genome.region, cell_class: genome.cell_class, cell_type: genome.cell_type,
      side: genome.side, flow: genome.flow, fired_count: genome.firedCount,
      synapses: genome.synapses, spiked: genome.spiked, potentials: genome.potentials,
    },
    context: 'one real fired neuron of the sealed BANC v888 Drosophila brain on Arc (chain 5042)',
  }
  try {
    const res = await fetch(JEV_API, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({ model: 'jev-latest', state, questions: jevQuestions(genome) }),
    })
    if (!res.ok) throw new Error(`JEV ${res.status}`)
    const data = await res.json()
    if (typeof data !== 'object' || data === null || data.answers === undefined) {
      throw new Error('response missing answers')
    }
    return { ...data, source: 'jev-latest (typesafe.ai System One)' }
  } catch (e) {
    // D8: JEV being down must never block a hatch - deterministic template
    return { ...renderSpecimenTemplate(genome), source: `template fallback: ${e.message}` }
  }
}
